import { Reveal } from "./Reveal";
import { Link } from "react-router-dom";
import { roleSlug } from "@/data/roles";
import {
  Network,
  Brain,
  Database,
  FlaskConical,
  BarChart3,
  CloudCog,
  Cloud,
  GitBranch,
  Activity,
  Settings,
  LucideIcon,
} from "lucide-react";

type Role = { name: string[]; Icon: LucideIcon };

const DATA_ROLES: Role[] = [
  { name: ["Data", "Architect"], Icon: Network },
  { name: ["Machine Learning", "Engineer"], Icon: Brain },
  { name: ["Data", "Engineer"], Icon: Database },
  { name: ["Data", "Scientist"], Icon: FlaskConical },
  { name: ["Analytics", "Engineer"], Icon: BarChart3 },
];

const CLOUD_ROLES: Role[] = [
  { name: ["Cloud / Solutions", "Architect"], Icon: CloudCog },
  { name: ["Cloud", "Engineer"], Icon: Cloud },
  { name: ["DevOps", "Engineer"], Icon: GitBranch },
  { name: ["Site Reliability", "Engineer"], Icon: Activity },
  { name: ["MLOps", "Engineer"], Icon: Settings },
];

const RoleCard = ({ role, tone, index }: { role: Role; tone: "orange" | "teal"; index: number }) => {
  const { Icon } = role;
  return (
    <Link to={`/roles/${roleSlug(role.name.join(" "))}`} className="block h-full">
    <div
      data-testid={`role-card-${role.name.join("-").toLowerCase().replace(/[\s/]+/g, "-")}`}
      className={`group relative flex min-h-[150px] flex-col items-center justify-center gap-4 overflow-hidden rounded-2xl px-3 py-7 text-center shadow-lg transition-transform duration-500 ease-out hover:-translate-y-1.5 ${
        tone === "orange"
          ? "bg-brand-orange shadow-brand-orange/25"
          : "bg-brand-teal shadow-brand-teal/25"
      }`}
    >
      {tone === "teal" && (
        <span className="absolute inset-x-0 top-0 h-1.5 bg-brand-orange" aria-hidden="true" />
      )}
      <Icon
        size={34}
        strokeWidth={1.4}
        className="text-white transition-transform duration-500 ease-out group-hover:scale-110"
      />
      <span className="font-cond text-[13px] font-semibold uppercase leading-snug tracking-wide text-white">
        {role.name.map((line) => (
          <span key={line} className="block">
            {line}
          </span>
        ))}
      </span>
    </div>
    </Link>
  );
};

export default function Roles() {
  return (
    <section id="talent" className="mx-auto max-w-6xl px-5 py-10 sm:px-8 sm:py-16" data-testid="roles-section">
      <Reveal className="text-center">
        <h2 className="font-display text-3xl font-extrabold uppercase tracking-wide text-brand-orange sm:text-4xl">
          Data / Cloud
        </h2>
        <p className="mt-3 text-base font-medium text-brand-teal sm:text-lg">
          Ten roles. All senior. All vetted for your stack.
        </p>
      </Reveal>

      {[DATA_ROLES, CLOUD_ROLES].map((rowRoles, rowIdx) => (
        <div
          key={rowIdx}
          className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5"
          data-testid={`roles-row-${rowIdx + 1}`}
        >
          {rowRoles.map((role, i) => (
            <Reveal key={role.name.join(" ")} delay={0.08 * i}>
              <RoleCard role={role} tone={rowIdx === 0 ? "orange" : "teal"} index={i} />
            </Reveal>
          ))}
        </div>
      ))}
    </section>
  );
}
